/**
 * Indexability helpers shared by client SEO, SSR, and sitemap generation.
 * Keep this file free of Vite `import.meta` so Node scripts can import it.
 */

/** Vue Router `name` values that must not appear in search results. */
export const NOINDEX_ROUTE_NAMES = new Set([
    "cart",
    "login",
    "registerPage",
    "verifyPage",
    "gest-profile-page",
    "user-profile-page",
    "my-orders",
    "order-message",
    "tamara-callback",
    "tamara-status-callback",
    "addresses",
    "map",
    "cart-confirmation",
    "fast-cart-confirmation",
    "online-payment",
    "fast-delivery-address",
    "fast-delivery-tracking-order",
]);

/**
 * Fast-delivery grocery / produce categories that leak generic SERP queries
 * (كيوي، كمثرى، خس، بصل…) and should stay in the app but out of the index.
 */
const GROCERY_CATEGORY_RE =
    /خضار|خضروات|فواكه|فاكهة|ورقيات|بصل|خس|كيوي|كمثرى|تفاح|موز|طماط|خيار|جزر|بطاطس| grocer|fruit|veggie|vegetable|produce/i;

const GROCERY_PRODUCT_RE =
    /كيوي|كمثرى|خس|بصل|تفاح|موز|طماط|خيار|جزر|بطاطس|كوسة|فلفل|ليمون|برتقال|عنب|فراولة|kiwi|pear|lettuce|onion|apple|banana|tomato|cucumber|carrot|potato/i;

export function isGroceryCategoryName(name) {
    return GROCERY_CATEGORY_RE.test(String(name || ""));
}

export function isGroceryProductName(name) {
    return GROCERY_PRODUCT_RE.test(String(name || ""));
}

/**
 * @param {{ isFastDelivery?: boolean, categoryName?: string }} [ctx]
 */
export function isNonIndexableCatalogProduct(product, ctx = {}) {
    const cat = ctx.categoryName || "";
    const pname = product?.name || "";
    if (isGroceryCategoryName(cat) || isGroceryProductName(pname)) {
        return true;
    }
    return false;
}

export function robotsForRouteName(routeName) {
    if (NOINDEX_ROUTE_NAMES.has(routeName)) {
        return "noindex, follow";
    }
    return "index, follow";
}

/**
 * @param {object | null | undefined} product
 * @param {{ isFastDelivery?: boolean, categoryName?: string }} [ctx]
 */
export function robotsForProduct(product, ctx = {}) {
    if (!product?.name) {
        return "noindex, follow";
    }
    if (isNonIndexableCatalogProduct(product, ctx)) {
        return "noindex, follow";
    }
    return "index, follow";
}

export function robotsForCategory(category) {
    if (!category?.name) {
        return "noindex, follow";
    }
    if (isGroceryCategoryName(category.name)) {
        return "noindex, follow";
    }
    return "index, follow";
}

/** Gregorian Arabic date for visible blog timestamps (schema stays ISO). */
export function formatDisplayDate(iso) {
    if (!iso) return "";
    const s = String(iso);
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) {
        return s.slice(0, 16).replace("T", " ");
    }
    return new Intl.DateTimeFormat("ar-SA", {
        dateStyle: "long",
        calendar: "gregory",
    }).format(d);
}
